import React, { createContext, useContext, useCallback, useRef } from 'react';
import { logger } from '@/utils/productionLogger';

interface PerformanceContextType {
  measureRender: (componentName: string) => () => void;
  measureApi: (operationName: string) => () => void;
  reportMetric: (name: string, value: number, unit?: string) => void;
}

const PerformanceContext = createContext<PerformanceContextType | null>(null);

export const PerformanceProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  const metricsRef = useRef(new Map<string, number>());

  const measureRender = useCallback((componentName: string) => {
    const start = performance.now();
    
    return () => {
      const duration = performance.now() - start;
      logger.performance(componentName, duration);
      
      // Store metric for potential analytics
      metricsRef.current.set(`render_${componentName}`, duration);
    };
  }, []);

  const measureApi = useCallback((operationName: string) => {
    const start = performance.now();
    
    return () => {
      const duration = performance.now() - start;
      logger.api(operationName, duration);
      
      // Store metric for potential analytics
      metricsRef.current.set(`api_${operationName}`, duration);
    };
  }, []);

  const reportMetric = useCallback((name: string, value: number, unit = 'ms') => {
    metricsRef.current.set(name, value);
    
    // Only log significant metrics
    if (value > 100) {
      logger.info(`Metric: ${name} = ${value}${unit}`);
    }
  }, []);

  const value = {
    measureRender,
    measureApi,
    reportMetric
  };

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};